// The story in the sim (gate D1.3): @rpg/story's story and @rpg/dialogue's conversations as one kernel module on the
// streamed valley, its state in its own save slice and in the hash.
//
// One sim step, by system order in the one kernel game:
//   story:clock   (-45)  the story clock
//   (things:interact -20: a press on a townsperson talks: the story's talk, then the ink conversation opens)
//   story:feed    (170)  deeds into the story: defeats (combat:defeated, with the enemy's kind, region and the tag a
//                        quest gave it), gathering (the things' events), regions entered; then quest groups waiting for
//                        their ground are spawned, the story settles (quests, objectives, triggers) and a townsperson
//                        near the hero may bark
// Events out (seam 1): `story:quest` on every quest change, `story:scene` when the story asks for a cutscene (and a
// conversation waits on it until `story:scene-done`), `dialogue:open`, `dialogue:line` and `dialogue:close`; a bark is a
// `dialogue:line` with no conversation open.
// Providers (decision 0055): the quest source, affinity, the event log and the dialogue runner are passed in (`providers`),
// the standard ones by default; the provider-swap test replaces each.
import type { GameModule, World as KernelWorld } from "@rpg/kernel";
import type { Interaction } from "@rpg/controller";
import type { NavWorld } from "@rpg/nav";
import type { World } from "@rpg/world";
import {
  BarkDirector,
  Conditions,
  createStory,
  mapMemory,
  ringEventLog,
  scoreAffinity,
  staticQuests,
  tieredStanding,
  type AffinityProvider,
  type EventLogProvider,
  type FactSource,
  type FactValue,
  type QuestSource,
  type Story,
  type StorySnapshot,
} from "@rpg/story";
import { resolveMarker } from "@rpg-unit/quest-journal";
import { createConversations, createInkRunner, MissingStringIdError, storyBindings, type Conversations, type DialogueRunner } from "@rpg/dialogue";
import type { GameEvents } from "../core";
import type { Fight } from "../enemies/fight";
import type { Things } from "../things/module";
import type { ThingsContent } from "../things/content";
import type { StoryContent, SpawnGroup } from "./content";
import { heroFacts, shapeCentre, type RegionShape } from "./facts";
import { isFresh } from "../save/fresh";

export const STORY_C = "story/State";
/** The save slice's version. */
export const STORY_SLICE_VERSION = 1;

/** The providers a test (or an AI-directed story) may replace; each defaults to the standard one. */
export interface StoryProviders {
  quests?: QuestSource;
  affinity?: AffinityProvider;
  log?: EventLogProvider;
  /** The dialogue runner, given the story (its bindings) and a host for game actions. */
  runner?: (story: Story, host: (name: string, args: Record<string, FactValue>) => boolean | void, npc: () => string | null) => DialogueRunner;
}

/** The townsfolk as story reads them (phase D joined: the town's, by NPC id). */
export interface StoryNpcs {
  ids(): readonly string[];
  /** Where one stands (its feet), or null while it is out of sight (indoors). */
  at(id: string): [number, number, number] | null;
  /** Which way it faces. */
  yaw(id: string): number;
  /** Its display name (a string id). */
  name(id: string): string;
  /** What it says when there is nothing to talk about with it (a string id), or null. */
  greeting(id: string): string | null;
}

/** The rpg layer as story's game actions reach it (phase D joined): items and gold given and taken, a shop opened. */
export interface StoryRpg {
  /** Give the player items (`gold` to the purse). */
  give(item: string, count: number): void;
  /** Take items (`gold` from the purse); false, nothing taken, when there aren't enough. */
  take(item: string, count: number): boolean;
  /** Open an NPC's shop screen (a trade choice). */
  shop(npc: string): void;
}

/** What the scene gives the story. */
export interface StoryHost {
  content: StoryContent;
  /** Compiled ink scripts by name. */
  scripts: Readonly<Record<string, string>>;
  /** A string's words (the strings table): a line's text for `dialogue:line`. */
  text(id: string): string;
  world: World;
  things: () => Things;
  thingsContent: ThingsContent;
  fight: () => Fight;
  nav: () => NavWorld;
  hero(): { x: number; y: number; z: number };
  hours(): number;
  day(): number;
  npcs: StoryNpcs;
  /** The rpg layer (give, take, shops). */
  rpg: StoryRpg;
  /** The one condition language the other layers register their facts on (rpg's level, items and gold; town's hour
   *  and the townsfolk's places); the story adds its own. Default a fresh one. */
  conditions?: Conditions;
  providers?: StoryProviders;
}

interface Pending {
  group: string;
}

/** The module's own saved state. */
interface LayerState {
  t: number;
  region: string | null;
  /** Tags on spawned enemies: fighter id → tag. */
  tags: Record<string, string>;
  pending: Pending[];
  /** A townsperson with nothing to talk about greeting the hero: who, and when the line ends. */
  greet: { npc: string; until: number } | null;
}

/** A bark said (for the handle; not saved). */
export interface BarkSaid {
  t: number;
  speaker: string;
  bark: string;
  line: string;
  deed: string | null;
}

/** Build the story layer. */
export function createStoryLayer(h: StoryHost) {
  const c = h.content;
  const npcIds = (): readonly string[] => h.npcs.ids();
  const npcAt = (id: string): [number, number, number] | null => h.npcs.at(id);
  let world: KernelWorld | null = null;
  const fresh = (): LayerState => ({ t: 0, region: null, tags: {}, pending: [], greet: null });
  // the things' step last read (the things' own count: a page's, not saved)
  let thingsStep = 0;
  let ls = fresh();
  let emit: (<K extends keyof GameEvents>(name: K, payload: GameEvents[K]) => void) | null = null;
  const said: BarkSaid[] = [];
  const queued: GameEvents["combat:defeated"][] = [];

  // --- facts ---------------------------------------------------------------------------------------------------------
  // one condition language: the rpg layer's and the town's facts are already on it (phase D joined)
  const conditions = h.conditions ?? new Conditions();
  const regions = h.world.layout.regions;
  const points = new Map<string, [number, number]>();
  for (const p of h.world.placements()) points.set(p.id, [p.pos[0], p.pos[1]]);
  for (const g of c.groups) points.set(g.id, g.at);
  conditions.register(heroFacts({ regions: regions as readonly { id: string; shape: RegionShape }[], spots: points, hero: () => h.hero(), regionAt: (x, z) => h.world.terrain.regionAt(x, z) }));

  // --- the story -----------------------------------------------------------------------------------------------------
  let conv: Conversations | null = null;
  const talking = (): string | null => conv?.state().npc ?? null;
  const host = (name: string, args: Record<string, FactValue>): boolean | void => {
    if (name === "scene") {
      const scene = String(args.scene ?? args.arg);
      // a conversation open waits on the scene the script asked for
      if (conv?.active()) conv.wait(scene);
      emit?.("story:scene", { scene });
      return;
    }
    if (name === "spawn") {
      const g = String(args.group);
      if (!c.groups.some((x) => x.id === g)) throw new Error(`story: no spawn group "${g}"`);
      ls.pending.push({ group: g });
      return;
    }
    if (name === "give") return void h.rpg.give(String(args.item), Number(args.count ?? 1));
    if (name === "take") return h.rpg.take(String(args.item), Number(args.count ?? 1));
    // a trade choice opens the NPC's shop (the rpg layer's screen, priced with the town's standing)
    if (name === "shop") return void h.rpg.shop(String(args.npc ?? args.arg ?? talking() ?? ""));
    throw new Error(`story: no host action "${name}"`);
  };
  const p = h.providers ?? {};
  const story = createStory({
    quests: p.quests ?? staticQuests(c.quests),
    affinity: p.affinity ?? scoreAffinity(c.affinity),
    standing: tieredStanding(c.factions),
    log: p.log ?? ringEventLog({ cap: c.logCap }),
    memory: mapMemory(),
    conditions,
    vars: c.vars,
    arcs: c.arcs,
    triggers: c.triggers,
    deedRules: c.deedRules,
    deedKinds: c.deedKinds,
    talk: c.talk,
    witnesses: { radius: c.witnessRadius, npcs: () => npcIds(), at: npcAt },
    clock: () => ls.t,
    day: () => h.day(),
    host,
    hosts: c.hosts,
  });
  story.onQuest((q) => emit?.("story:quest", { quest: q.quest, state: q.state, objective: q.objective }));
  const runner = p.runner ? p.runner(story, host, talking) : createInkRunner({ scripts: h.scripts, bindings: storyBindings(story, { npc: talking, host }), seed: 7 });
  conv = createConversations(runner, {
    text: (id) => h.text(id),
    onOpen: (npc, script) => emit?.("dialogue:open", { npc, script }),
    onLine: (l) => emit?.("dialogue:line", l),
    onClose: (npc) => emit?.("dialogue:close", { npc }),
  });
  const conversations = conv;
  const barks = new BarkDirector(c.barks, c.barkGates);
  /** Lines that reached the player without a string id (the harness's invariant reports them; the talk ends). */
  const problems: string[] = [];
  const guarded = (fn: () => void): void => {
    try {
      fn();
    } catch (e) {
      if (!(e instanceof MissingStringIdError)) throw e;
      problems.push(e.message);
      conversations.close();
    }
  };

  /** Talk with a townsperson: the story's talk (memory, the day's affinity, the log), then the conversation. */
  const talk = (npc: string): void => {
    const d = c.dialogues.find((x) => x.npc === npc);
    if (conversations.active() || ls.greet) return;
    if (!d) {
      // a townsperson with no script (the ambient ones): a greeting line, the talk closed after its time on screen
      const line = h.npcs.greeting(npc);
      if (!line) return;
      story.talk(npc);
      const text = h.text(line);
      const seconds = 1.2 + 0.045 * text.length;
      ls.greet = { npc, until: ls.t + seconds };
      emit?.("dialogue:open", { npc, script: line });
      emit?.("dialogue:line", { speaker: npc, stringId: line, text, seconds });
      return;
    }
    story.talk(npc);
    guarded(() => conversations.open(npc, d.script, d.knot));
  };

  // --- spawning a quest's group once its ground is there --------------------------------------------------------------
  const groupOf = (id: string): SpawnGroup => c.groups.find((g) => g.id === id)!;
  const trySpawn = (g: SpawnGroup): boolean => {
    const y = h.world.heightAt(g.at[0], g.at[1]);
    const near = h.nav().nearest([g.at[0], y, g.at[1]]);
    if (!near || Math.hypot(near.point[0] - g.at[0], near.point[2] - g.at[1]) > 4) return false;
    let i = 0;
    for (const e of g.enemies) {
      for (let k = 0; k < e.count; k++, i++) {
        const a = i * 2.39996;
        const r = g.spread * Math.sqrt(i / Math.max(1, g.enemies.reduce((n, x) => n + x.count, 0)));
        const id = h.fight().spawn(e.kind, g.at[0] + Math.sin(a) * r, g.at[1] + Math.cos(a) * r);
        ls.tags[String(id)] = g.tag;
      }
    }
    return true;
  };

  // --- the snapshot (the save and the hash) ---------------------------------------------------------------------------
  const snapshot = () => ({ layer: structuredClone(ls), story: story.snapshot(), conversation: conversations.snapshot(), barks: barks.snapshot() });
  // a new game's story (the v6 migration's fresh marker restores it: a save from before the story joined the valley)
  let initial: ReturnType<typeof snapshot> | null = null;
  const restore = (data: ReturnType<typeof snapshot>): void => {
    const d = isFresh(data) ? initial! : data;
    ls = { ...fresh(), ...structuredClone(d.layer) };
    story.restore(d.story as StorySnapshot);
    conversations.restore(d.conversation);
    barks.restore(d.barks);
    queued.length = 0;
    // what the things logged before the restore is not this save's
    thingsStep = h.things().steps();
  };

  const module: GameModule<GameEvents> = {
    id: "story",
    version: "0.1.0",
    // checked declarations (decision 0077): what it emits
    provides: { events: ["story:quest", "story:scene", "dialogue:open", "dialogue:line", "dialogue:close"] },
    dependencies: ["things"],
    setup(ctx) {
      emit = (name, payload) => ctx.events.emit(name, payload);
      ctx.events.on("combat:defeated", (e) => void queued.push(e));
      ctx.events.on("story:scene-done", ({ scene }) => {
        conversations.release(scene);
        story.record({ kind: "scene", subject: scene });
      });
      // a press on a townsperson (the town's interaction, phase D joined) talks with them
      ctx.events.on("npc:interact", ({ npc }) => talk(npc));
      ctx.registerSystem(
        ({ dt }) => {
          ls.t += dt;
          if (ls.greet && ls.t >= ls.greet.until) {
            const npc = ls.greet.npc;
            ls.greet = null;
            emit?.("dialogue:close", { npc });
          }
        },
        { name: "story:clock", order: -45 },
      );
      ctx.registerSystem(
        () => {
          const hero = h.hero();
          // defeats this step: the enemy's kind, where, and its quest tag
          for (const e of queued.splice(0)) {
            const en = h.fight().state().enemies.find((x) => x.id === e.entity);
            if (!en) continue;
            const pos: [number, number, number] = [en.pos[0], en.pos[1], en.pos[2]];
            const place = h.world.terrain.regionAt(pos[0], pos[2]);
            const tag = ls.tags[String(en.id)];
            story.record({ kind: "defeated", subject: en.kind, pos, ...(place ? { place } : {}), ...(tag ? { tag } : {}) });
          }
          // gathering: the things' events since the last step
          const th = h.things();
          const thStep = th.steps();
          for (const ev of th.events) {
            if (ev.step <= thingsStep || ev.type !== "gathered") continue;
            const at = th.at(ev.id);
            const place = h.world.terrain.regionAt(at[0], at[2]);
            for (const g of ev.got) story.record({ kind: "gathered", subject: g.item, pos: at, data: { count: g.count }, ...(place ? { place } : {}) });
          }
          thingsStep = thStep;
          // regions entered
          const region = h.world.terrain.regionAt(hero.x, hero.z);
          if (region !== ls.region) {
            ls.region = region;
            if (region) story.record({ kind: "entered", subject: region, place: region });
          }
          // quest groups waiting for their ground
          ls.pending = ls.pending.filter((pd) => !trySpawn(groupOf(pd.group)));
          story.settle();
          // a bark from a townsperson near the hero, when no conversation is open (every quarter second)
          if (!conversations.active() && Math.round(ls.t * 120) % 30 === 0) {
            let best: { npc: string; d: number } | null = null;
            for (const n of npcIds()) {
              const at = npcAt(n);
              if (!at) continue;
              const d = Math.hypot(at[0] - hero.x, at[2] - hero.z);
              if (d <= c.barkRadius && (!best || d < best.d)) best = { npc: n, d };
            }
            if (best) {
              const pick = barks.pick(best.npc, ls.t, { conditions, log: story.log, pos: npcAt(best.npc)! });
              if (pick) {
                const text = h.text(pick.bark.line);
                emit!("dialogue:line", { speaker: best.npc, stringId: pick.bark.line, text, seconds: 1.2 + 0.045 * text.length });
                said.push({ t: ls.t, speaker: best.npc, bark: pick.bark.id, line: pick.bark.line, deed: pick.deed ? `${pick.deed.kind}:${pick.deed.subject}` : null });
                if (said.length > 64) said.splice(0, said.length - 48);
              }
            }
          }
        },
        { name: "story:feed", order: 170 },
      );
      ctx.registerAction("story/talk", (pl) => talk(String((pl as { npc: string }).npc)));
      ctx.registerAction("story/advance", () => guarded(() => conversations.advance()));
      ctx.registerAction("story/choose", (pl) => guarded(() => conversations.choose(Number((pl as { n: number }).n))));
      // a new game (the title's, on its first play): the opening's trigger waits for it; `{ opening: false }` starts the
      // game as after the opening (the main line offered, no scene: `?opening=off`, for checks that don't watch it)
      ctx.registerAction("story/new-game", (pl) => {
        if ((pl as { opening?: boolean } | undefined)?.opening === false) story.set("story.opening-seen", true);
        else story.set("story.new-game", true);
      });
      ctx.registerAction("story/track", (pl) => story.track(((pl as { quest: string | null }).quest ?? null) as string | null));
      ctx.registerSaveSlice({
        version: STORY_SLICE_VERSION,
        serialize: () => JSON.parse(JSON.stringify(snapshot())),
        deserialize: (d) => restore(d as ReturnType<typeof snapshot>),
      });
    },
  };

  initial = snapshot();
  return {
    module,
    hashed: [STORY_C] as const,
    build(w: KernelWorld): void {
      world = w;
      // the hash reads the whole story through a getter: providers keep their own state
      const view = {
        get state() {
          return snapshot();
        },
      };
      w.addComponent(w.createEntity(), STORY_C, view);
    },
    /** The spots a quest group waits to spawn on (the world keeps their chunks loaded). */
    pins: (): { x: number; z: number }[] => ls.pending.map((pd) => {
      const g = groupOf(pd.group);
      return { x: g.at[0], z: g.at[1] };
    }),
    story,
    conversations,
    barks,
    said: (): BarkSaid[] => said.map((b) => ({ ...b })),
    problems: (): string[] => [...problems],
    talk,
    /** The objective marker: the tracked quest's current objective, its label (a string id) and where it points. */
    objective(): { quest: string; objective: string; label: string; marker: string | null; pos: [number, number, number] | null } | null {
      const o = story.objective();
      if (!o) return null;
      // where its marker points (@rpg-unit/quest-journal): an NPC where they stand, a region's centre, a spawn group, a place
      const pos = resolveMarker(
        o.marker,
        {
          npc: (id) => ((p) => (p ? ([p[0], p[2]] as [number, number]) : null))(npcAt(id)),
          region: (id) => ((r) => (r ? shapeCentre(r.shape) : null))(regions.find((r) => r.id === id)),
          group: (id) => c.groups.find((g) => g.id === id)?.at ?? null,
          place: (id) => points.get(id) ?? null,
        },
        (x, z) => h.world.heightAt(x, z),
      );
      return { ...o, pos };
    },
    /** The layer's own state (for the handle). */
    state: () => ({ t: ls.t, region: ls.region, pending: ls.pending.map((x) => x.group), tags: { ...ls.tags }, greet: ls.greet ? { ...ls.greet } : null }),
    snapshot,
    world: () => world,
  };
}

/** The story layer. */
export type StoryLayer = ReturnType<typeof createStoryLayer>;
